import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Check, X } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLES = {
  pending: "bg-champagne/10 text-champagne border-champagne/30",
  approved: "bg-[#4D5B46]/30 text-[#A3C293] border-[#4D5B46]/50",
  rejected: "bg-[#5C2B29]/30 text-[#E5A09E] border-[#5C2B29]/50",
};

const VaelithonReign = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("integrations.manage");
  const canApprove = hasPermission("approvals.approve");
  const [status, setStatus] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([
        api.get("/vaelithon/status"),
        api.get("/vaelithon/drafts"),
      ]);
      setStatus(s.data);
      setDrafts(d.data);
    } catch (err) {
      if (err.response?.status !== 403) toast.error("Could not load Vaelithon Reign");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleConnection = async () => {
    try {
      await api.post("/vaelithon/connection", null, { params: { enabled: !status?.connected, endpoint: status?.connection?.endpoint || "" } });
      toast.success("Updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const decide = async (draft, decision) => {
    const note = window.prompt(`Optional note:`, "");
    try {
      await api.post(`/vaelithon/drafts/${draft.id}/decision`, { decision, note: note || "" });
      toast.success(`${decision}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout>
      <section className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="label-eyebrow">AI Governance</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Vaelithon Reign</h1>
          <p className="text-textmuted mt-3 max-w-2xl leading-relaxed">
            The AI intelligence and execution layer that augments R.S.V.P. Vaelithon Reign submits
            drafts — tasks, reports, recommendations. <span className="text-champagne">Aurethion Crown remains authoritative</span>:
            every Vaelithon Reign action passes through your approval.
          </p>
        </div>
        {canManage && (
          <button
            onClick={toggleConnection}
            data-testid="toggle-vaelithon-btn"
            className={`inline-flex items-center gap-2 self-start px-5 py-2.5 rounded-md text-sm font-medium transition-all ${
              status?.connected ? "bg-[#4D5B46]/40 text-[#A3C293] border border-[#4D5B46]/60" : "bg-champagne text-obsidian hover:bg-champagne-soft"
            }`}
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.6} />
            {status?.connected ? "Connected" : "Connect Vaelithon Reign"}
          </button>
        )}
      </section>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-champagne animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            {["pending", "approved", "rejected"].map((k) => (
              <div key={k} className="regal-card p-5">
                <p className="label-eyebrow">{k}</p>
                <p className="font-sans text-3xl font-light mt-2">{status?.counts?.[k] ?? 0}</p>
              </div>
            ))}
          </div>

          <h2 className="font-heading text-2xl mb-3">Pending drafts</h2>
          {drafts.length === 0 ? (
            <div className="regal-card p-10 text-center text-textmuted text-sm">
              Vaelithon Reign has not submitted anything yet. When connected, its drafts appear here for
              your review before any change is applied.
            </div>
          ) : (
            <div className="regal-card overflow-hidden">
              <div className="divide-y divide-seam">
                {drafts.map((d) => (
                  <div key={d.id} className="px-5 py-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLES[d.status] || STATUS_STYLES.pending}`}>
                            {d.status}
                          </span>
                          <span className="text-[10px] uppercase tracking-wider text-champagne">{d.kind}</span>
                        </div>
                        <h3 className="font-heading text-lg mt-1">{d.title}</h3>
                        {d.body && <p className="text-xs text-textmuted mt-1">{d.body}</p>}
                      </div>
                      {canApprove && d.status === "pending" && (
                        <div className="flex gap-2">
                          <button onClick={() => decide(d, "approve")} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-champagne text-obsidian font-medium hover:bg-champagne-soft">
                            <Check className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button onClick={() => decide(d, "reject")} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-seam text-textmuted hover:text-[#E5A09E]">
                            <X className="w-3.5 h-3.5" /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Layout>
  );
};

export default VaelithonReign;
